import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Clock, MapPin, Camera, Smartphone, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  // Redirect desktop users to auth page
  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        navigate('/auth');
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, [navigate]);

  const features = [
    {
      icon: Clock,
      title: 'Fit Your Schedule',
      description: 'Whether you have 5 minutes or 2 hours, find the perfect activity to fill the gap instantly.',
      number: '01'
    },
    {
      icon: MapPin,
      title: 'Go Hyper-Local',
      description: "Don't settle for generic advice. Join city hubs to discover real-world activities happening near you.",
      number: '02'
    },
    {
      icon: Camera,
      title: 'See It, Snap It',
      description: 'Browse a visual feed of what others are doing and use the instant camera mode to share your own adventures.',
      number: '03'
    },
    {
      icon: Smartphone,
      title: 'No Download Needed',
      description: 'Experience the smooth, tactile feel of a native mobile app directly in your browser—zero install required.',
      number: '04'
    }
  ];

  return (
    <div className="min-h-screen bg-surface lg:hidden">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-white px-6 pt-8 pb-12"
      >
        {/* Logo */}
        <div className="mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_0639c081-d9f0-47e7-918b-f72657c5b82c/artifacts/6zzokka9_boredom%20buster%20logo.svg" 
            alt="BoredomBuster" 
            className="h-10 w-auto"
          />
        </div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-4xl font-bold text-grayDark mb-3 leading-tight"
        >
          Never Be Bored Again
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-gray text-lg mb-8 leading-relaxed"
        >
          Discover activities tailored to your time, location, and mood. Share your adventures with a vibrant community.
        </motion.p>

        {/* Primary CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-14 bg-primary hover:bg-primary/90 text-primaryDark font-bold rounded-lg shadow-lg text-base flex items-center justify-center gap-2"
          >
            Get Started
            <ArrowRight className="w-5 h-5" />
          </Button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center text-gray text-sm mt-4"
        >
          Already have an account?{' '}
          <button
            onClick={() => navigate('/auth')}
            className="text-primaryDark font-bold hover:underline"
          >
            Sign In
          </button>
        </motion.p>
      </motion.div>

      {/* Illustration Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="px-6 py-8"
      >
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-cardBorder">
          <img 
            src="https://customer-assets.emergentagent.com/job_noboredom-1/artifacts/4gttuy91_hero%20signupmodal.svg"
            alt="BoredomBuster illustration"
            className="w-full h-auto"
          />
        </div>
      </motion.div>

      {/* Features Section */}
      <div className="px-6 py-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-2xl font-bold text-grayDark mb-2"
        >
          How It Works
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="text-gray mb-8 leading-relaxed"
        >
          Four simple ways BoredomBuster transforms your downtime
        </motion.p>

        <div className="space-y-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.number}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + (index * 0.1), duration: 0.5 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-cardBorder"
              >
                <div className="flex items-start gap-4">
                  {/* Number Badge */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primaryDark" strokeWidth={2} />
                    </div>
                    <div className="text-center mt-2">
                      <span className="text-xs font-bold text-grayMeta">{feature.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-grayDark mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bottom CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="px-6 py-8 pb-12"
      >
        <div className="bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl p-8 text-center border border-primary/30">
          <h3 className="text-2xl font-bold text-grayDark mb-3">
            Ready to Beat Boredom?
          </h3>
          <p className="text-gray mb-6 leading-relaxed">
            Join thousands discovering fun activities every day
          </p>
          
          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-14 bg-primary hover:bg-primary/90 text-primaryDark font-bold rounded-lg shadow-lg text-base flex items-center justify-center gap-2 mb-3"
          >
            Create Free Account
            <ArrowRight className="w-5 h-5" />
          </Button>

          <button
            onClick={() => navigate('/auth')}
            className="text-grayDark text-sm font-semibold hover:underline"
          >
            Already have an account? Sign In
          </button>
        </div>
      </motion.div>

      {/* Footer */}
      <div className="px-6 py-8 text-center border-t border-cardBorder">
        <p className="text-gray text-xs">
          © 2024 BoredomBuster. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LandingPage;
